/**
 * Highlight titles must be unique within a credit proposal: a title already
 * used by one highlight is not offered to the others, and duplicates saved
 * before this rule existed are flagged so they can be fixed before saving.
 *
 * A highlight is identified by its picklist code; highlights saved with a
 * free-text title and no code are compared by title (case/space-insensitive).
 */

export interface HighlightTitleItem {
  code?: number | string | null;
  title?: string | null;
}

export interface HighlightTitleOption {
  value: number | string;
  label: string;
}

function normalizeTitle(value: unknown): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Identity of a highlight's title, or '' when no title is chosen yet. */
export function highlightTitleKey(item: HighlightTitleItem | null | undefined): string {
  if (item?.code !== null && item?.code !== undefined && item.code !== '') {
    return `code:${Number(item.code)}`;
  }

  const title = normalizeTitle(item?.title);
  return title ? `title:${title}` : '';
}

/** Picklist options not used by any highlight other than `current`. */
export function availableHighlightOptions<T extends HighlightTitleOption>(
  options: T[],
  items: HighlightTitleItem[],
  current: HighlightTitleItem | null,
): T[] {
  const usedCodes = new Set<number>();
  const usedTitles = new Set<string>();

  items.forEach((item) => {
    if (item === current) {
      return;
    }

    if (item?.code !== null && item?.code !== undefined && item.code !== '') {
      usedCodes.add(Number(item.code));
    }

    const title = normalizeTitle(item?.title);
    if (title) {
      usedTitles.add(title);
    }
  });

  return options.filter(
    (option) => !usedCodes.has(Number(option.value)) && !usedTitles.has(normalizeTitle(option.label)),
  );
}

/**
 * Highlights that repeat a title already used by an earlier highlight. The
 * first one keeps the title; the later ones are the duplicates to change.
 */
export function duplicateHighlightTitles<T extends HighlightTitleItem>(items: T[]): Set<T> {
  const seen = new Set<string>();
  const duplicates = new Set<T>();

  items.forEach((item) => {
    const keys = [highlightTitleKey(item), normalizeTitle(item?.title) ? `title:${normalizeTitle(item?.title)}` : '']
      .filter(Boolean);

    if (!keys.length) {
      return;
    }

    if (keys.some((key) => seen.has(key))) {
      duplicates.add(item);
    }

    keys.forEach((key) => seen.add(key));
  });

  return duplicates;
}

/** True while at least one title is still free for a new highlight. */
export function canAddHighlight(options: HighlightTitleOption[], items: HighlightTitleItem[]): boolean {
  if (!options.length) {
    return true;
  }

  const free = availableHighlightOptions(options, items, null).length;
  const waitingForTitle = items.filter((item) => !highlightTitleKey(item)).length;

  return free > waitingForTitle;
}
